import type { LeaveRequest } from "@spork/s4p-server-sdk";

import { getLeaveRequestPriority } from "@/features/home/leaveRequestStatus";
import { addCalendarDays, toDateOnlyIso } from "@/lib/date";

export const LEAVE_REQUEST_WINDOW_LOOKBACK_DAYS = 1;
export const LEAVE_REQUEST_WINDOW_FORWARD_DAYS = 21;
export const LEAVE_REQUEST_WINDOW_SPAN_DAYS =
    LEAVE_REQUEST_WINDOW_LOOKBACK_DAYS + LEAVE_REQUEST_WINDOW_FORWARD_DAYS + 1;

export type LeaveRequestDateWindow = {
    startIso: string;
    endIso: string;
};

export type LeaveRequestOverviewSections = {
    actionNeeded: LeaveRequest[];
    period: LeaveRequest[];
};

/**
 * Rolling overview window: yesterday through today + 21 days, shifted by
 * whole 23-day spans so adjacent windows neither overlap nor leave a gap.
 */
export function getLeaveRequestOverviewWindow(
    todayIso: string,
    windowShift: number,
): LeaveRequestDateWindow {
    const offsetDays = windowShift * LEAVE_REQUEST_WINDOW_SPAN_DAYS;

    return {
        startIso: addCalendarDays(
            todayIso,
            -LEAVE_REQUEST_WINDOW_LOOKBACK_DAYS + offsetDays,
        ),
        endIso: addCalendarDays(
            todayIso,
            LEAVE_REQUEST_WINDOW_FORWARD_DAYS + offsetDays,
        ),
    };
}

/** True when the leave interval overlaps [windowStartIso, windowEndIso]. */
export function leaveRequestOverlapsWindow(
    request: Pick<LeaveRequest, "date_from" | "date_to">,
    windowStartIso: string,
    windowEndIso: string,
): boolean {
    const dateFrom = toDateOnlyIso(request.date_from);
    const dateTo =
        request.date_to == null || request.date_to === ""
            ? null
            : toDateOnlyIso(request.date_to);

    return (
        dateFrom <= windowEndIso && (dateTo == null || dateTo >= windowStartIso)
    );
}

export function filterLeaveRequestsByChild(
    requests: LeaveRequest[],
    childId: number | null,
): LeaveRequest[] {
    if (childId == null) {
        return requests;
    }

    return requests.filter((request) => request.child_id === childId);
}

function sortByDateFromAsc(left: LeaveRequest, right: LeaveRequest): number {
    const dateDiff = toDateOnlyIso(left.date_from).localeCompare(
        toDateOnlyIso(right.date_from),
    );

    if (dateDiff !== 0) {
        return dateDiff;
    }

    return left.id - right.id;
}

function sortActionNeeded(left: LeaveRequest, right: LeaveRequest): number {
    const priorityDiff =
        getLeaveRequestPriority(left.status) -
        getLeaveRequestPriority(right.status);

    if (priorityDiff !== 0) {
        return priorityDiff;
    }

    return sortByDateFromAsc(left, right);
}

/**
 * Split leave requests that overlap the selected window.
 * `needs_info` goes to action needed; everything else to the period list.
 */
export function splitLeaveRequestsForOverview(
    requests: LeaveRequest[],
    options: {
        windowStartIso: string;
        windowEndIso: string;
    },
): LeaveRequestOverviewSections {
    const actionNeeded: LeaveRequest[] = [];
    const period: LeaveRequest[] = [];

    for (const request of requests) {
        if (
            !leaveRequestOverlapsWindow(
                request,
                options.windowStartIso,
                options.windowEndIso,
            )
        ) {
            continue;
        }

        if (request.status === "needs_info") {
            actionNeeded.push(request);
        } else {
            period.push(request);
        }
    }

    actionNeeded.sort(sortActionNeeded);
    period.sort(sortByDateFromAsc);

    return { actionNeeded, period };
}
